import { ColumnSort, Row, SortingFn, SortingState } from '@tanstack/react-table';
import { sort } from 'radash';

import { DataModelAttribute, UiCollectionSorter, UiCollectionSorterOption } from '@/types/DataModel';

const isBlankSortValue = (value: unknown) =>
    value === undefined || value === null || value === '';

/**
 * TanStack's default `sortUndefined: 1` puts blanks last only for ascending
 * sorts. Descending flips that, so a new empty row jumps to the top.
 * Pre-invert here so blanks stay last after that flip. `null` means both
 * values are filled and the real comparator should run.
 */
export const blanksLastSortResult = (
    aBlank: boolean,
    bBlank: boolean,
    isDesc: boolean,
): number | null => {
    if (!aBlank && !bBlank) {
        return null;
    }
    if (aBlank && bBlank) {
        return 0;
    }
    const blankAfter = aBlank ? 1 : -1;
    return isDesc ? -blankAfter : blankAfter;
};

export const withBlanksLast = <T,>(base: SortingFn<T> | 'auto'): SortingFn<T> => {
    const autoFns = new WeakMap<object, SortingFn<T>>();

    return (rowA: Row<T>, rowB: Row<T>, columnId: string) => {
        const aBlank = isBlankSortValue(rowA.getValue(columnId));
        const bBlank = isBlankSortValue(rowB.getValue(columnId));
        if (aBlank || bBlank) {
            const column = rowA.getAllCells().find((cell) => cell.column.id === columnId)?.column;
            return blanksLastSortResult(aBlank, bBlank, column?.getIsSorted() === 'desc') ?? 0;
        }
        if (typeof base === 'function') {
            return base(rowA, rowB, columnId);
        }
        const column = rowA.getAllCells().find((cell) => cell.column.id === columnId)?.column;
        if (!column) {
            return 0;
        }
        let autoFn = autoFns.get(column);
        if (!autoFn) {
            autoFn = column.getAutoSortingFn();
            autoFns.set(column, autoFn);
        }
        return autoFn(rowA, rowB, columnId);
    };
};

export const resolveUiCollectionSorterOption = (
    sortingOptions: UiCollectionSorter | undefined,
    attributeName: string,
): UiCollectionSorterOption | undefined => {
    if (!sortingOptions) {
        return undefined;
    }

    if (Array.isArray(sortingOptions)) {
        return sortingOptions.find((entry) => entry?.key === attributeName)?.value;
    }

    return sortingOptions[attributeName];
};

type GenerateInitialSortingState = (
    collectionAttribute: DataModelAttribute,
    memberAttributes: DataModelAttribute[],
) => SortingState;

export const generateInitialSortingState: GenerateInitialSortingState = (
    collectionAttribute,
    memberAttributes,
) => {

    const sortingOptions = collectionAttribute.params?.uiCollectionSorter;
    const sortingState: (ColumnSort & { priority: number })[] = [];

    if (sortingOptions) {

        memberAttributes.forEach((attribute, index) => {

            const sortingOption = resolveUiCollectionSorterOption(sortingOptions, attribute.name);

            if (sortingOption) {
                const id = attribute.name;
                const sortDescFirst = sortingOption.initialDirection === 'desc';
                const priority = sortingOption.sortPriority || index;

                sortingState.push({
                    id,
                    desc: sortDescFirst,
                    priority,
                });
            }

        });

    }

    return sort(sortingState, state => state.priority);
};
